import ArchiveIcon from '@material-ui/icons/Archive';
import React, { useCallback } from 'react';
import {
  Datagrid,
  List,
  Responsive,
  TextInput,
  SimpleList,
  TopToolbar,
  TextField,
  FunctionField,
  useQuery,
  Error,
  Pagination,
  useListContext
} from 'react-admin';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  Button
} from '@material-ui/core/';
import moment from 'moment';
import { saveAs } from 'file-saver';

import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import ListTitleBar from '../../core/components/ListTitleBar';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import GridPopover from '../../core/components/grid/GridPopover';
import EmailArchiveAttachmentApi from '../../core/apis/EmailArchiveAttachmentApi';
import DateInput from '../../core/components/forked/input/DateInput';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import PopupField from '../../core/components/grid/PopupField';

const useStyles = makeStyles({
  rootList: {
    '& form:first-child': {
      alignItems: 'flex-start'
    }
  },
  textInput: {
    minWidth: '225px'
  },
  dataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        display: 'none'
      }
    }
  },
  tbody: {
    '& td:nth-of-type(1)': {
      display: 'none'
    }
  },
  root: {
    marginRight: 'auto'
  },
  attachments: {
    display: 'flex',
    justifyContent: 'center'
  },
  button: {
    marginRight: '0px'
  },
  buttonDownload: {
    fontSize: 14,
    fontWeight: 400,
    color: '#2962FF',
    marginRight: '16px',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  rootCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 'none'
  },
  iconButton: {
    borderLeft: '1px solid #E0E0E0',
    borderRadius: '0px'
  },
  input: {
    '&::before': {
      zIndex: 1
    }
  },
  inputContainer: {
    '& p': {
      marginTop: '4px',
      whiteSpace: 'nowrap'
    }
  },
  dateFilterStyle: {
    display: 'flex',
    marginBottom: '4px'
  },
  dateInputStyle: {
    width: '130px',
    marginRight: '5px'
  },
  dateRangeSeparator: {
    marginTop: '25px',
    marginRight: '5px',
    color: '#757575'
  },
  centerProgress: {
    textAlign: 'center'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();

  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const formatStartDate = value => {
  const mmt = moment(value);
  return mmt.isValid()
    ? mmt
        .startOf('day')
        .utc()
        .toISOString()
    : null;
};

const formatEndDate = value => {
  const mmt = moment(value);
  return mmt.isValid()
    ? mmt
        .endOf('day')
        .utc()
        .toISOString()
    : null;
};

const isValidDate = value => {
  const dateValue = moment(value);
  if (dateValue.isValid() && dateValue.isAfter(moment().endOf('day'))) {
    return 'ra.notification.date_after_today';
  }
  return undefined;
};

const DateRangeFilter = props => {
  const classes = useStyles();
  const maxDate = moment(props.filterValues.endDate).subtract(1, 'day');
  const minDate = props.filterValues.startDate;

  return (
    <div className={classes.dateFilterStyle}>
      <DateInput
        className={classes.dateInputStyle}
        label="Select Date Range"
        source="startDate"
        customParseValue={formatStartDate}
        placeholder="From:"
        maxDate={maxDate}
        disableFuture
        validate={isValidDate}
      />
      <Typography variant="body1" className={classes.dateRangeSeparator}>
        -
      </Typography>
      <DateInput
        className={classes.dateInputStyle}
        label=""
        source="endDate"
        customParseValue={formatEndDate}
        utcMode
        placeholder="To:"
        minDate={minDate}
        disableFuture
        validate={isValidDate}
      />
    </div>
  );
};

const EmailArchiveFilters = props => {
  const classes = useStyles();
  return (
    <Filter variant="standard" {...props}>
      <TextInput
        label="Search by Subject"
        source="subject"
        alwaysOn
        className={classes.textInput}
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
      />
      <DateRangeFilter {...props} alwaysOn source="daterangeusedforkey" />
      <TextInput
        label="Search by Fund Name"
        source="fundName"
        alwaysOn
        className={classes.textInput}
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
      />
    </Filter>
  );
};

const EmailArchiveTitle = () => {
  return <ListTitleBar type="Email Archive" icon={<ArchiveIcon />} />;
};

const Attachments = ({ id: emailArchiveId, classes }) => {
  const { data, loading, error } = useQuery({
    type: 'getManyReference',
    resource: 'emailarchiveattachments',
    payload: {
      target: 'emailArchiveId',
      id: emailArchiveId
    }
  });
  const notifyServerError = useNotifyServerError();
  const downloadAttachment = useCallback(
    async attachment => {
      try {
        const response = await EmailArchiveAttachmentApi.downloadAttachmentFile(
          attachment
        );
        saveAs(response.data, attachment.fileName);
      } catch (error) {
        notifyServerError(error);
      }
    },
    [notifyServerError]
  );

  if (loading) return <CircularProgress />;
  if (error) return <Error />;
  if (!data) return null;

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Attachment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.values(data).map((attachment, index) => (
            <TableRow key={index}>
              <TableCell classes={{ root: classes.rootCell }}>
                {attachment.fileName}
                <Button
                  onClick={() => downloadAttachment(attachment)}
                  className={classes.buttonDownload}
                >
                  Download
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

const BlockedDatagrid = ({ children, ...props }) => {
  const classes = useStyles();
  const listContext = useListContext();
  if (listContext && listContext.loaded && !listContext.loading) {
    return <Datagrid {...props}>{children}</Datagrid>;
  }
  return (
    <div className={classes.centerProgress}>
      <CircularProgress />
    </div>
  );
};

const EmailArchiveList = props => {
  const classes = useStyles();
  const {
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    userProfile,
    ...rest
  } = props;

  return (
    <List
      {...rest}
      sort={{ field: 'timestamp', order: 'DESC' }}
      exporter={false}
      title={<EmailArchiveTitle hasCreatePermission={hasCreatePermission} />}
      filters={<EmailArchiveFilters />}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
      actions={<Actions />}
      empty={<Empty />}
      classes={{ root: classes.rootList }}
      syncWithLocation
    >
      <Responsive
        small={
          <SimpleList
            primaryText={record => record.subject}
            secondaryText={record => record.id}
            tertiaryText={() => ''}
          />
        }
        medium={
          <BlockedDatagrid
            classes={{ thead: classes.dataGridHead, tbody: classes.tbody }}
            isRowSelectable={() => false}
          >
            <LinkField label="Subject" source="subject" linkType="show" />
            <FunctionField
              render={record =>
                record.emailSentFromSystem ||
                (!record.userFirstName && !record.userLastName)
                  ? 'System'
                  : record.userDeleted
                  ? `${record.userFirstName ? record.userFirstName : ''} ${
                      record.userLastName ? record.userLastName : ''
                    } (D)`
                  : `${record.userFirstName ? record.userFirstName : ''} ${
                      record.userLastName ? record.userLastName : ''
                    }`
              }
              sortBy="userFirstName"
              label="Sender"
              id="sender"
            />
            <TextField
              source="noOfRecipients"
              label="Recipients"
              sortable={false}
              id={'recipients'}
            />
            <TextField
              source="listName"
              label="Distribution List Name"
              sortable={false}
              id="distribution list name"
            />
            <FunctionField
              label="Fund Name"
              id="fund name"
              render={record => {
                const funds = record.fundNames
                  ? Object.values(record.fundNames)
                  : [];
                if (funds && funds.length > 1) {
                  return (
                    <div>
                      <PopupField
                        data={funds}
                        label="Multiple Funds"
                        headLabel="Fund Name"
                      />
                    </div>
                  );
                } else if (funds && funds.length === 1) {
                  return <Typography variant="body2">{funds[0]}</Typography>;
                } else return <Typography variant="body2"> </Typography>;
              }}
            />
            <FunctionField
              label="Date Sent"
              source="timestamp"
              id="date sent"
              render={record =>
                moment(record.timestamp).format('MM/DD/YYYY [at] hh:mm A')
              }
            />
            <FunctionField
              className={classes.attachments}
              label="Attachments"
              id="attachments"
              render={record =>
                record.noOfAttachments > 0 && (
                  <GridPopover
                    text={record.noOfAttachments}
                    multiple
                    classes={{ button: classes.button }}
                    popoverContent={
                      <Attachments classes={classes} id={record.id} />
                    }
                  />
                )
              }
            />
          </BlockedDatagrid>
        }
      />
    </List>
  );
};

export default EmailArchiveList;
